import { Checkbox } from '@fluentui/react';
import { IVerticalStackedChartProps, IVSChartDataPoint, VerticalStackedBarChart } from '@fluentui/react-charting';
import AppContext from 'App/AppContext';
import { IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import TenantPicker, { PickerTenant, TenantPickerMode } from 'components/Pickers/TenantPicker';
import { globalStackTokensGapMedium } from 'globalStyles';
import RiskState, { RiskStates } from 'models/riskState';
import { isPartnerWidgetType, WidgetSizes, WidgetTenantConfig, WidgetWidth } from 'models/widget';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetDataTenantRiskStatus } from 'services/Api/dashboardService';
import { apiRequest } from 'services/Auth/authConfig';
import { enumKeyAtIndex } from 'utils/object';
import { getRiskStateColorGradient } from 'utils/risk';
import { sortOnNumber } from 'utils/sorting';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';
import {
  getConfiguredTenantIds,
  getTenantWidgetTenantById,
  getTenantWidgetTenantId,
  getTenantWidgetTenantName,
  sortTenantWidgetTenantsByName,
  TenantWidgetTenant,
  WithTenantId,
  withTenantId,
} from './tenantWidgetData';
import { useTenantDrillDown } from './useTenantDrillDown';

export class WidgetTenantRiskStatusConfig extends WidgetTenantConfig {
  filterGoalBelowCurrentNoOpenControls: boolean = false;

  constructor() {
    super();
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.loadTenant(newRawConfig);
        this.filterGoalBelowCurrentNoOpenControls = newRawConfig.filterGoalBelowCurrentNoOpenControls ?? false;
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetTenantRiskStatusConfig {
    const newConfig = this.cloneTenant() as WidgetTenantRiskStatusConfig;
    newConfig.filterGoalBelowCurrentNoOpenControls = this.filterGoalBelowCurrentNoOpenControls;

    return newConfig;
  }
}

interface IWidgetTenantRiskStatusProps extends IWidgetRendererProps {}

const WidgetTenantRiskStatus = (props: IWidgetTenantRiskStatusProps) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = useContext(AppContext);
  const [data, setData] = useState<RiskState[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [colorGradient] = useState<string[]>(getRiskStateColorGradient());
  const [tenants, setTenants] = useState<TenantWidgetTenant[]>([]);
  const [tenantsToShow, setTenantsToShow] = useState<TenantWidgetTenant[]>([]);
  const drillDown = useTenantDrillDown();

  useEffect(() => {
    const loadData = async () => {
      try {
        if (isLoading) return;
        setIsLoading(true);

        await appContext.globalDataCache.tags.getItems();
        const config = loadConfig();

        const tenants: TenantWidgetTenant[] = isPartnerWidgetType(props.widget.widgetType)
          ? props.sharedData?.partnerCustomers ?? []
          : props.sharedData?.userOrgUnits ?? [];
        setTenants(tenants);
        const tenantIds = getConfiguredTenantIds(config.tenants, tenants);
        if (tenantIds.length === 0) {
          setData([]);
          setTenantsToShow([]);

          return;
        }

        const accessToken = await appContext.getAccessToken(apiRequest.scopes);

        const data = await apiGetWidgetDataTenantRiskStatus(
          tenantIds,
          config.filterGoalBelowCurrentNoOpenControls,
          accessToken,
          props.abortSignal,
        );

        setData(data);

        const tenantsToShow = tenants.filter((tenant) =>
          data.some((s) => s.tenantId === getTenantWidgetTenantId(tenant)),
        );
        sortTenantWidgetTenantsByName(tenantsToShow);
        setTenantsToShow(tenantsToShow);
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget, props.sharedData?.partnerCustomers, props.sharedData?.userOrgUnits]);

  const loadConfig = (): WidgetTenantRiskStatusConfig => {
    const newConfig = new WidgetTenantRiskStatusConfig();
    newConfig.load(props.widget.widgetConfig);

    return newConfig;
  };

  const getRiskStateColor = (idx: number): string => {
    return colorGradient[idx];
  };

  const getData = (): IVerticalStackedChartProps[] => {
    if (data.length === 0 || tenantsToShow.length === 0) return [];

    const graphData: WithTenantId<IVerticalStackedChartProps>[] = tenantsToShow.map((tenant) => {
      const tenantId = getTenantWidgetTenantId(tenant);
      const tenantData = data
        .filter((s) => s.tenantId === tenantId)
        .sort((a, b) => sortOnNumber(a.sortOrder, b.sortOrder));
      const tenantName = getTenantWidgetTenantName(tenant);

      const dataPoints: IVSChartDataPoint[] = tenantData.map((s, idx) => {
        return {
          legend: s.state,
          data: s.riskCount ?? 0,
          color: getRiskStateColor(idx),
          xAxisCalloutData: tenantName,
        };
      });

      return {
        chartData: withTenantId(dataPoints, tenantId),
        xAxisPoint: tenantName,
        tenantId,
      };
    });

    return graphData;
  };

  const getFilterForColor = (color: string | undefined): string => {
    if (!color) return '';
    const idx = colorGradient.findIndex((c) => c === color);
    if (idx < 0) return '';

    return enumKeyAtIndex(RiskStates, idx);
  };

  const onClick = (data: IVSChartDataPoint) => {
    const tenant = getTenantWidgetTenantById(tenants, (data as WithTenantId<IVSChartDataPoint>).tenantId);
    if (!tenant) return;
    const filter = getFilterForColor(data.color);
    let url = '/risks/overview';
    if (filter) {
      url += `?status=${filter}`;
    }
    drillDown(tenant, url, isPartnerWidgetType(props.widget.widgetType));
  };

  //
  // Main render
  //
  if (!data || isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (!isLoading && (data.length === 0 || tenantsToShow.length === 0)) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('translation:General.Notifications.NoItemsFilter')}</IPText>
      </IPStack>
    );
  }

  const shouldRotateXAxisLabels = tenantsToShow.length > 10 && props.widget.widgetSize !== WidgetSizes.Large;

  return (
    <IPStack verticalFill>
      <IPStack.Item grow>
        <VerticalStackedBarChart
          data={getData()}
          height={250}
          width={WidgetWidth[props.widget.widgetSize] - 80}
          wrapXAxisLables={!shouldRotateXAxisLabels}
          rotateXAxisLables={shouldRotateXAxisLabels}
          barWidth={'auto'}
          legendProps={{
            centerLegends: true,
          }}
          onBarClick={(ev, data) => onClick(data as IVSChartDataPoint)}
        />
      </IPStack.Item>
    </IPStack>
  );
};

export default WidgetTenantRiskStatus;

//
// Config
//
interface IWidgetConfigWidgetTenantRiskStatusProps extends IWidgetConfigRendererProps {}

export const WidgetConfigTenantRiskStatus = (props: IWidgetConfigWidgetTenantRiskStatusProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'widgets']);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
  const [filterGoal, setFilterGoal] = useState<boolean>(false);
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetTenantRiskStatusConfig => {
    let newRawConfig = new WidgetTenantRiskStatusConfig();
    if (props.widget.widgetConfig) {
      newRawConfig = JSON.parse(props.widget.widgetConfig);
    }

    return newRawConfig;
  };

  const updateTenants = (items: PickerTenant[]) => {
    const tenants = items.map((i) => i.tenantId);
    setSelectedTenants(tenants);
    const config = getConfig(tenants, filterGoal);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const updateFilterGoal = (checked: boolean) => {
    setFilterGoal(checked ?? false);
    const config = getConfig(selectedTenants, checked);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      const config = loadConfig();
      setSelectedTenants(config.tenants);
      setFilterGoal(config.filterGoalBelowCurrentNoOpenControls);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getConfig = (items: string[], filterGoal: boolean): WidgetTenantRiskStatusConfig => {
    const config = new WidgetTenantRiskStatusConfig();
    config.tenants = items;
    config.filterGoalBelowCurrentNoOpenControls = filterGoal;

    return config;
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <TenantPicker
          mode={isPartnerWidgetType(props.widget.widgetType) ? TenantPickerMode.Partner : TenantPickerMode.User}
          onSelect={updateTenants}
          selectedTenants={selectedTenants}
          emptySelectionMeansAll
        />
      </IPStack.Item>
      <IPStack.Item>
        <Checkbox
          label={t('widgets:Risk.FilterScoreGoalBelowCurrentNoOpenControls')}
          checked={filterGoal}
          onChange={(ev, checked) => updateFilterGoal(checked ?? false)}
        />
      </IPStack.Item>
    </IPStack>
  );
};
